"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const disciplines = [{
    numeral: "I.",
    title: "OTA Revenue Management",
    french: "Strategic Channel Optimization",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_revenue_management.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_revenue_management.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_revenue_management.webp-wvc-sizes",
    alt: "Sunil Singh analyzing OTA channel performance data on a dashboard",
    paragraphs: ["We transform your OTA presence into a high-performing revenue engine. By leveraging data-driven insights, we optimize your listings to capture maximum demand while maintaining rate integrity across all platforms.", "Our approach focuses on visibility, conversion, and profitability. We manage your channel mix to ensure you are not just getting bookings, but the right bookings that align with your property's revenue goals.", "From content optimization to dynamic inventory management, we ensure your hotel stands out in a crowded marketplace, driving direct and indirect revenue growth through precision-targeted OTA strategies."],
    technique: "Data-Driven Strategy",
    context: "Global OTA Platforms",
    duration: "Ongoing Support",
    materials: "Channel Manager, PMS"
  }, {
    numeral: "II.",
    title: "Hospitality Tech Implementation",
    french: "Tech Stack Optimization",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hospitality_tech_stack.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hospitality_tech_stack.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hospitality_tech_stack.png-wvc-sizes",
    alt: "Modern hotel technology stack integration and software dashboard",
    paragraphs: ["Your technology stack is the backbone of your revenue management. We specialize in recommending and implementing the right tools—from PMS to Channel Managers—that streamline your operations.", "We bridge the gap between complex software and operational efficiency. Our team ensures your systems are integrated seamlessly, reducing manual errors and providing real-time data for better decision-making.", "Whether you are upgrading your existing infrastructure or starting from scratch, we provide the technical expertise to ensure your hotel is equipped with the most efficient and scalable solutions available."],
    technique: "System Integration",
    context: "Modern Hotel Tech",
    duration: "4–8 weeks",
    materials: "PMS, RMS, CRS"
  }, {
    numeral: "III.",
    title: "Revenue Audits",
    french: "Performance Analysis",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png-wvc-sizes",
    alt: "Professional revenue audit report showing growth metrics",
    paragraphs: ["Unlock hidden revenue potential with our comprehensive audits. We deep-dive into your current performance, pricing strategies, and market positioning to identify gaps and opportunities for growth.", "Our audits provide actionable insights that go beyond surface-level metrics. We analyze your competitive set, demand patterns, and historical data to build a roadmap for sustained revenue improvement.", "We deliver a clear, data-backed report that empowers you to make informed decisions. Our goal is to provide you with the clarity needed to maximize your property's financial performance in any market condition."],
    technique: "Performance Benchmarking",
    context: "Market Analysis",
    duration: "2–4 weeks",
    materials: "Market Data, PMS Reports"
  }];
  return <section data-section-id="3027" id="servicenavigationmenu" className="bg-slate-50 text-slate-900 font-default py-32 md:py-40 px-6 md:px-10">
      <div ref={header.ref} className={`max-w-3xl mx-auto text-center transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="text-2xl text-blue-600 mb-8" aria-hidden="true" style={{
        letterSpacing: "0.1em"
      }}>
          &#9670;
        </div>
        <p className="font-sans text-xs uppercase mb-10 text-slate-500" style={{
        letterSpacing: "0.3em"
      }}>
          &mdash; Expertise &middot; Our Services
        </p>
        <h2 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-slate-900">
          Data-Driven
          <br />
          Revenue
          <br />
          <span className="text-blue-600">Growth Strategies.</span>
        </h2>
        <p className="mt-12 max-w-xl mx-auto text-lg md:text-xl leading-relaxed text-slate-600">
          At HotelRevBaBa, we provide specialized revenue management consulting designed to maximize your property's profitability through proven, modern, and scalable strategies.
        </p>
      </div>

      <div className="mt-32 md:mt-40 max-w-7xl mx-auto">
        {disciplines.map((d, i) => {
        const reversed = i % 2 === 1;
        const {
          ref,
          isVisible
        } = useRevealOnScroll<HTMLDivElement>({
          threshold: 0.12
        });
        return <div key={i} data-index={i}>
              <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
                <div className={`relative ${reversed ? "lg:order-2" : "lg:order-1"}`}>
                  <div className="relative">
                    <div className="absolute -bottom-3 -right-3 md:-bottom-4 md:-right-4 w-full h-full border border-blue-600/20" aria-hidden="true"></div>
                    <div className="relative overflow-hidden bg-slate-200 rounded-lg shadow-lg">
                      <img src={d.image} data-wvc-srcset={d.srcset} data-wvc-sizes={d.sizes} alt={d.alt} loading="lazy" decoding="async" className="w-full h-full object-cover aspect-[5/6]" />
                    </div>
                  </div>
                </div>

                <div className={reversed ? "lg:order-1" : "lg:order-2"}>
                  <div className="font-default text-blue-600 text-6xl md:text-7xl lg:text-[80px] leading-none mb-6" aria-hidden="true">
                    {d.numeral}
                  </div>
                  <h3 className="font-default text-3xl md:text-4xl lg:text-[44px] leading-tight text-slate-900">
                    {d.title}
                  </h3>
                  <p className="font-default text-base text-blue-700 mt-2">
                    {d.french}
                  </p>

                  <div className="mt-8 space-y-5">
                    {d.paragraphs.map((p, pi) => <p data-index={pi} key={pi} className="text-base md:text-[17px] leading-[1.8] text-slate-700">
                        {p}
                      </p>)}
                  </div>

                  <div className="mt-12 border-t border-slate-200 pt-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6">
                      {[{
                    label: "Methodology",
                    value: d.technique
                  }, {
                    label: "Market Focus",
                    value: d.context
                  }, {
                    label: "Engagement",
                    value: d.duration
                  }, {
                    label: "Core Tools",
                    value: d.materials
                  }].map((spec, si) => <div data-index={si} key={si} className="md:border-r md:last:border-r-0 border-slate-200 md:pr-6">
                          <p className="font-sans text-[10px] uppercase text-slate-500 mb-2" style={{
                      letterSpacing: "0.2em"
                    }}>
                            {spec.label}
                          </p>
                          <p className="font-default text-sm md:text-base text-slate-900 leading-snug">
                            {spec.value}
                          </p>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>

              {i < disciplines.length - 1 && <div className="flex items-center justify-center my-24 md:my-32" aria-hidden="true">
                  <span className="h-px flex-1 bg-slate-200"></span>
                  <span className="px-6 text-blue-600 text-lg">&#9670;</span>
                  <span className="h-px flex-1 bg-slate-200"></span>
                </div>}
            </div>;
      })}
      </div>
    </section>;
}